import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const AscendingDescendingNumbers: React.FC = () => {
    const [question, setQuestion] = useState("Sort the numbers in:");
    const [numbers, setNumbers] = useState<number[]>([]);
    const [correctOrder, setCorrectOrder] = useState<number[]>([]);
    const [options, setOptions] = useState<number[][]>([]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Ascending and Descending Order Worksheet for LKG | Number Ordering | Mama’s Guidance";
    }, []);

    const generateQuestion = () => {
        const type = Math.random() > 0.5 ? "ascending" : "descending";
        setQuestion(`Sort the numbers in ${type} order:`);

        const uniqueNumbers = new Set<number>();
        while (uniqueNumbers.size < 5) {
            uniqueNumbers.add(Math.floor(Math.random() * 40) + 11);
        }
        const randomNumbers = Array.from(uniqueNumbers);

        const sortedNumbers = type === "ascending" ? [...randomNumbers].sort((a, b) => a - b) : [...randomNumbers].sort((a, b) => b - a);

        setNumbers(randomNumbers);
        setCorrectOrder(sortedNumbers);

        // Generate unique incorrect options
        const incorrectOrders = new Set<string>();
        while (incorrectOrders.size < 2) {
            const shuffled = [...randomNumbers].sort(() => Math.random() - 0.5);
            if (JSON.stringify(shuffled) !== JSON.stringify(sortedNumbers)) {
                incorrectOrders.add(JSON.stringify(shuffled));
            }
        }

        const optionsArray = shuffleArray([sortedNumbers, ...Array.from(incorrectOrders).map(str => JSON.parse(str))]);
        setOptions(optionsArray);
        setMessage("");
    };

    const shuffleArray = (array: number[][]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const checkAnswer = (selected: number[]) => {
        if (JSON.stringify(selected) === JSON.stringify(correctOrder)) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
            }, 5000)
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce LKG children to ascending and descending order with fun and interactive activities on Mama’s Guidance—designed to develop number sequencing and comparison skills through engaging visual exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Ascending and Descending Order</h1>

                                    <p>
                                        Ordering numbers helps children understand number values and sequences. This <strong>LKG worksheet on ascending and descending order</strong> introduces kids to arranging numbers from smallest to largest and largest to smallest using bright, fun-filled visuals.
                                    </p>

                                    <h2>🔼 What Is Ascending and Descending Order?</h2>
                                    <ul>
                                        <li><strong>Ascending Order:</strong> Arranging numbers from <strong>small to big</strong> (e.g., 3, 5, 7, 9)</li>
                                        <li><strong>Descending Order:</strong> Arranging numbers from <strong>big to small</strong> (e.g., 10, 8, 6, 4)</li>
                                    </ul>

                                    <p>
                                        These concepts help children build logical thinking, sorting skills, and a better understanding of number sequences.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify and compare numbers from 1 to 100</li>
                                        <li>Arrange numbers in ascending (↑) and descending (↓) order</li>
                                        <li>Understand how numbers increase or decrease in value</li>
                                        <li>Improve pattern recognition and analytical thinking</li>
                                    </ul>

                                    <h2>📋 What&apos;s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Arrange groups of 3–5 numbers in ascending or descending order</li>
                                        <li>Cut-and-paste activities with number cards</li>
                                        <li>Visual guides with arrows (↑ for ascending, ↓ for descending)</li>
                                        <li>Number stairs and mountain formats to show climbing up or down</li>
                                    </ul>

                                    <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                    <ul>
                                        <li>Use building blocks or stackable toys to represent rising or falling numbers</li>
                                        <li>Draw staircases and write numbers on each step to visualize order</li>
                                        <li>Play “Number Train” game – ask the child to arrange coaches (numbers) in order</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Place toys or snacks in front of your child and ask them to count aloud. For example, <strong>“Can you give me 5 grapes?”</strong> This makes counting part of everyday learning!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Ascending and Descending Order</h1>

                                <p>
                                    Ordering numbers helps children understand number values and sequences. This <strong>LKG worksheet on ascending and descending order</strong> introduces kids to arranging numbers from smallest to largest and largest to smallest using bright, fun-filled visuals.
                                </p>

                                <h2>🔼 What Is Ascending and Descending Order?</h2>
                                <ul>
                                    <li><strong>Ascending Order:</strong> Arranging numbers from <strong>small to big</strong> (e.g., 3, 5, 7, 9)</li>
                                    <li><strong>Descending Order:</strong> Arranging numbers from <strong>big to small</strong> (e.g., 10, 8, 6, 4)</li>
                                </ul>

                                <p>
                                    These concepts help children build logical thinking, sorting skills, and a better understanding of number sequences.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify and compare numbers from 1 to 100</li>
                                    <li>Arrange numbers in ascending (↑) and descending (↓) order</li>
                                    <li>Understand how numbers increase or decrease in value</li>
                                    <li>Improve pattern recognition and analytical thinking</li>
                                </ul>

                                <h2>📋 What&apos;s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Arrange groups of 3–5 numbers in ascending or descending order</li>
                                    <li>Cut-and-paste activities with number cards</li>
                                    <li>Visual guides with arrows (↑ for ascending, ↓ for descending)</li>
                                    <li>Number stairs and mountain formats to show climbing up or down</li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                <ul>
                                    <li>Use building blocks or stackable toys to represent rising or falling numbers</li>
                                    <li>Draw staircases and write numbers on each step to visualize order</li>
                                    <li>Play “Number Train” game – ask the child to arrange coaches (numbers) in order</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/skip-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Place toys or snacks in front of your child and ask them to count aloud. For example, <strong>“Can you give me 5 grapes?”</strong> This makes counting part of everyday learning!
                                </p>
                            </article>
                            <h2 style={{ fontSize: "1rem" }}>{question}</h2>
                            <div>
                                <h3>{numbers.join(", ")}</h3>
                            </div>
                            <div className="mt-4 mb-5 " style={{ marginLeft: 0, display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {options.map((option, index) => (
                                    <div style={{ width: 'auto', padding: '20px' }} className=" background-blue" key={index} onClick={() => checkAnswer(option)}>
                                        {option.join(", ")}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default AscendingDescendingNumbers;
